import Section from '../../../utils/section/Section';
import Toggle from '../../../utils/Toggle';
import MapLocation from '../../../utils/googleMaps/MapLocation';
import HostInfo from '../../../components/hostInfo/components/HostInfo';
import HostDetails from '../../../components/hostDetails/HostDetails';
import HostTeaserSmallList from '../../../components/hostTeaser/HostTeaserSmallList';
import AvailabilityWidgetContainer from '../../../components/availabilityWidget/containers/AvailabilityWidgetContainer';
import AvailabilityRequestContainer from '../../../components/availabilityRequest/containers/AvailabilityRequestContainer';
import Meta from '../../../utils/meta/Meta';
import HostSlider from '../../../components/hostSlider/components/HostSlider';
import GtmLastMinuteSection from '../../../features/gtmLastMinuteIntegration/GtmLastMinuteSection';
import { Accommodation, HostState } from '../../../routes/reducers/types';

import './Host.css';

type HostProps = Omit<HostState, 'bookings'> & { locale: string };

const canonicalBaseUrl = 'https://www.casapilot.com';

export function buildCanonicalHostPath(accommodation: Accommodation, locale: string): string {
  return `/${locale}/${encodeURIComponent(accommodation.slug)}`;
}

function buildCanonicalHostUrl(accommodation: Accommodation, locale: string): string {
  return `${canonicalBaseUrl}${buildCanonicalHostPath(accommodation, locale)}`;
}

function hasBreakfast(accommodation: Accommodation): boolean {
  return (
    accommodation.extra_service_conditions.find(
      (esc) => esc.extra_service_key === 'casapilot-breakfast',
    ) !== undefined
  );
}

export default function Host({
  property,
  related,
  sectionTitle,
  accommodation,
  meta,
  showReviews,
  locale,
}: HostProps): JSX.Element | null {
  if (
    property == null ||
    related == null ||
    sectionTitle == null ||
    accommodation == null ||
    meta == null
  ) {
    return null;
  }

  return (
    <div>
      <Meta
        title={meta.page_title}
        description={meta.page_description}
        logo={accommodation.attachments[0].url}
        canonicalURL={buildCanonicalHostUrl(accommodation, locale)}
      />
      <div data-host-id={property.user_id} />
      <HostSlider
        propertyId={property.id}
        images={accommodation.attachments}
        videos={accommodation.videos}
      />
      <Section highlight border small>
        <HostInfo
          accommodation={accommodation}
          sellingLine={property.selling_line}
          shortDisplayTitle={property.short_display_title}
          country={property.country}
          locality={property.locality}
          rating={property.rating}
          region={property.region}
        />
      </Section>
      <Section>
        <div className="host-availability-wrapper">
          <AvailabilityWidgetContainer property={property} accommodation={accommodation} />
          <AvailabilityRequestContainer />
        </div>
        <HostDetails.BasicInfo
          description_html={accommodation.description_html}
          info={accommodation.info}
          hasBreakfast={hasBreakfast(accommodation)}
        />
        <HostDetails.Amenities amenities={accommodation.amenities} />
        <HostDetails.Costs
          costs={accommodation.costs}
          extraOptions={accommodation.extraOptions}
          includedCosts={accommodation.includedCosts}
        />
        <HostDetails.ExtraOptions extraCosts={accommodation.extraCosts} />
        <HostDetails.AccommodationReviews showReviews={showReviews} />
        <HostDetails.Attractions attractions={accommodation.attractions} />
      </Section>
      <Toggle hide="xs sm">
        <Section small>
          <MapLocation longitude={accommodation.longitude} latitude={accommodation.latitude} />
        </Section>
      </Toggle>
      <GtmLastMinuteSection />
      <Section headline={sectionTitle.related}>
        <HostTeaserSmallList hosts={related} />
      </Section>
    </div>
  );
}

const defaultProps: Pick<
  HostProps,
  'property' | 'accommodation' | 'meta' | 'related' | 'sectionTitle'
> = {
  // eslint-disable-next-line react/default-props-match-prop-types
  property: undefined,
  // eslint-disable-next-line react/default-props-match-prop-types
  accommodation: undefined,
  // eslint-disable-next-line react/default-props-match-prop-types
  meta: undefined,
  // eslint-disable-next-line react/default-props-match-prop-types
  related: undefined,
  // eslint-disable-next-line react/default-props-match-prop-types
  sectionTitle: undefined,
};

Host.defaultProps = defaultProps;
